/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import moment from 'moment';
import { SessionType } from 'pages/sessions/interface';
import { IChatState, ISendMessageRequestDto, IUpdateGoalRequestDto } from './interface';

export const initialState: IChatState = {
  filters: {
    keyword: '',
    sessions: [],
    status: 'active',
  },
  loading: false,
  coversationLoading: false,
  sendMessageLoading: false,
  uplaoding: false,
  conversations: [],
  coversationId: null,
  conversation: {
    _id: '',
    title: '',
    users: [],
    experts: [],
    messages: [],
    createdAt: new Date().toString(),
    updatedAt: '',
    session: {
      _id: '',
      expertId: '',
      isActive: false,
      sessionType: SessionType.digitalSession,
      courseType: '',
      audienceType: '',
      title: '',
      description: '',
      message: '',
      language: '',
      expert: {
        id: '',
        firstName: '',
        lastName: '',
        profileImgUrl: '',
        bio: '',
      },
    },
    userSession: {
      _id: '',
      isCompleted: false,
      isCancel: false,
      startAt: moment().toISOString(),
      goals: [],
      isAllGoalsCompleted: false,
    },
  },
};

export const chatSlice = createSlice({
  name: 'feature/session/chat',
  initialState,
  reducers: {
    getConversations: (state: IChatState) => {
      state.loading = true;
    },
    getConversationsSucceeded: (
      state: IChatState,
      action: PayloadAction<IChatState['conversations']>,
    ) => {
      state.loading = false;
      const existingConversationsIds = state.conversations.map((message) => message._id);
      const newConversationsIds = action.payload.map((conversation) => conversation._id);
      if (!isEqual(existingConversationsIds, newConversationsIds.reverse())) {
        state.conversations = action.payload;
      }
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    getConversationsFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.loading = false;
    },
    setConversationId: (state: IChatState, action: PayloadAction<string | null>) => {
      state.coversationId = action.payload;
    },
    getSingleConversation: (state: IChatState) => {
      state.coversationLoading = true;
    },
    getSingleConversationSucceeded: (
      state: IChatState,
      action: PayloadAction<IChatState['conversation']>,
    ) => {
      state.coversationLoading = false;
      if (state.conversation._id === '') {
        state.conversation = action.payload;
        state.conversation.messages = action.payload.messages;
      } else {
        const existingMessageIds = state.conversation.messages.map((message) => message._id);
        const newMessageIds = action.payload.messages.map((message) => message._id);
        if (
          existingMessageIds.length === 0
          || newMessageIds.length === 0
          || !isEqual(existingMessageIds, newMessageIds)
        ) {
          state.conversation = action.payload;
          state.conversation.messages = action.payload.messages;
        }
      }

      if (action.payload.userSession.goals && action.payload.userSession.goals.length > 0) {
        const allResponseGoals = action.payload.userSession.goals;
        state.conversation.userSession.isAllGoalsCompleted = allResponseGoals.every(
          (goal) => Boolean(goal.isCompleted),
        );
        state.conversation.userSession.goals = allResponseGoals;
      }
    },
    getSingleConversationFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = false;
    },
    setStartUploading: (state: IChatState) => {
      state.uplaoding = true;
    },
    setEndUploading: (state: IChatState) => {
      state.uplaoding = false;
    },
    sendMessage: (state: IChatState, action: PayloadAction<ISendMessageRequestDto>) => {
      state.sendMessageLoading = true;
    },
    sendMessageSucceeded: (state: IChatState) => {
      state.sendMessageLoading = false;
    },
    sendMessageFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.sendMessageLoading = false;
    },
    completeSession: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = true;
    },
    completeSessionSucceeded: (state: IChatState) => {
      state.coversationLoading = false;
      state.conversation.userSession.isCompleted = true;
    },
    completeSessionFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = false;
    },
    setKeyword: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationId = null;
      state.filters.keyword = action.payload;
    },
    setStatus: (state: IChatState, action: PayloadAction<IChatState['filters']['status']>) => {
      state.coversationId = null;
      state.filters.status = action.payload;
    },
    setSessions: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationId = null;

      if (action.payload === 'all') {
        state.filters.sessions = [];
      } else {
        state.filters.sessions = [action.payload];
      }
    },
    clearFilters: (state: IChatState) => {
      state.filters = {
        keyword: '',
        sessions: [],
        status: 'all',
      };
    },
    updateGoals: (state: IChatState, action: PayloadAction<IUpdateGoalRequestDto>) => {
      state.coversationLoading = true;
      state.conversation.userSession.goals = state.conversation.userSession.goals.map((goal) => {
        goal.isCompleted = action.payload.ids.includes(goal._id);
        return goal;
      });
    },
    updateGoalsSucceeded: (state: IChatState) => {
      state.coversationLoading = false;
    },
    updateGoalsFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = false;
    },
    readAllConversationMessages: () => {},
  },
});

export const { actions: chatActions } = chatSlice;
